import { datadogRum } from '@datadog/browser-rum';
import { nextjsPlugin, onRouterTransitionStart } from '@datadog/browser-rum-nextjs';
import {
  DATADOG_ENV,
  DATADOG_SERVICE,
  DATADOG_SITE,
  DATADOG_VERSION,
} from '@/lib/datadog/constants';

export { onRouterTransitionStart };

const parseSampleRate = (v, def = 100) =>
  !v || isNaN(v) ? def : Math.min(100, Math.max(0, Number(v)));

const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;

// BL-970: crawlers that render JS (Googlebot, Bytespider...) were most of our RUM
// errors. Datadog only tags bots after ingesting the session, so they have to be
// matched here, by user agent, to keep them out of RUM and billing.
const BOT_USER_AGENT = /bot|crawl|spider|slurp|headless/i;

// Each predicate returning true means this page load is never sent to RUM.
const EXCLUDED_SESSIONS = [() => BOT_USER_AGENT.test(navigator.userAgent)];

// BL-979: scripts injected by the Facebook/Instagram iOS in-app browsers throw these
// after the user leaves the page. Gated by user agent so other web views still report them.
const META_IN_APP_BROWSER_USER_AGENT = /FBAN|FBAV|Instagram/;

const isMetaInAppBrowserError = (event) =>
  event.type === 'error' &&
  META_IN_APP_BROWSER_USER_AGENT.test(navigator.userAgent) &&
  /_AutofillCallbackHandler|window\.webkit\.messageHandlers/.test(event.error?.message);

// BL-975: Attentive's sign-up popup (loaded through GTM) calls preventDefault() in a
// passive touchmove listener, so Chromium on Android reports an intervention on every
// drag while it is open. It has no user impact and we can't change their script, so
// those reports are dropped here to keep them out of Error Tracking and billing.
// Matching on the source file keeps the same intervention visible if our code causes it.
const isAttentivePassiveIntervention = (event) =>
  event.type === 'error' &&
  event.error?.source === 'report' &&
  event.error?.type === 'PreventDefaultPassive' &&
  event.error?.stack?.includes('cdn.attn.tv/') === true;

// Each predicate returning true means this event is never sent to RUM.
const EXCLUDED_EVENTS = [isMetaInAppBrowserError, isAttentivePassiveIntervention];

const beforeSend = (event) => !EXCLUDED_EVENTS.some((isExcluded) => isExcluded(event));

// A sample rate of 0 excludes the entire session (views, resources, errors and replay).
const getSessionSampleRate = () =>
  EXCLUDED_SESSIONS.some((isExcluded) => isExcluded())
    ? 0
    : parseSampleRate(process.env.NEXT_PUBLIC_DATADOG_RUM_SAMPLE_RATE, 100);

// BL-520: without this, a missing env var means RUM silently never initialises —
// indistinguishable from "no errors happened". Name what is missing instead.
if (!applicationId || !clientToken) {
  const missing = [
    !applicationId && 'NEXT_PUBLIC_DATADOG_APPLICATION_ID',
    !clientToken && 'NEXT_PUBLIC_DATADOG_CLIENT_TOKEN',
  ].filter(Boolean);
  console.warn(`[Datadog] RUM not initialised. Missing: ${missing.join(', ')}`);
}

if (applicationId && clientToken) {
  datadogRum.init({
    applicationId,
    clientToken,
    site: DATADOG_SITE,
    service: DATADOG_SERVICE,
    env: DATADOG_ENV,
    version: DATADOG_VERSION,
    sessionSampleRate: getSessionSampleRate(),
    sessionReplaySampleRate: parseSampleRate(process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE, 20),
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend,
    plugins: [nextjsPlugin()],
  });
}

